import { IPromise } from 'angular';

import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { ServerFilterClauses } from '../helpers/ServerFilterClauses';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { ViewMapper } from '../mappers/ViewMapper';
import { ViewDefinition } from '../models/ContentDefinitions';
import { ITaggableContent } from '../models/ITaggableContent';
import { IViewBase, IViewDetailInfo, IViewInfo } from '../models/IViewInfo';
import { IWorkbookBase, IWorkbookParentInfo } from '../models/IWorkbookInfo';
import { Module } from '../Module';
import { AddTagsToViewsRequest } from '../ServerApi/requests/AddTagsToViewsRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetViewActionsRequest } from '../ServerApi/requests/GetViewActionsRequest';
import { GetViewByPathRequest, IGetViewByPathResult } from '../ServerApi/requests/GetViewByPathRequest';
import { GetViewsRequest } from '../ServerApi/requests/GetViewsRequest';
import { HideViewsRequest } from '../ServerApi/requests/HideViewsRequest';
import { MarkRecentlyViewedRequest, RecentlyViewedObjectType } from '../ServerApi/requests/MarkRecentlyViewedRequest';
import { RemoveTagsFromViewsRequest } from '../ServerApi/requests/RemoveTagsFromViewsRequest';
import { RenameViewRequest } from '../ServerApi/requests/RenameViewRequest';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { IResult } from '../ServerApi/types/IResponse';
import { IViewActions } from '../ServerApi/types/IViewActions';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export class Views implements IListResource<IViewInfo>, IFieldValuesResource, IDetailResource<IViewDetailInfo>, IActionResource<IViewActions>, ITaggableContent  {

  public static $inject = [
    '$q',
    'ErrorNotifyingServer',
    'FetcherFactory'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IViewInfo>;
  private allQuery: ResourceQuery<IViewInfo>;
  private detailFetcher: IDetailFetcher<IViewDetailInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor(
    private $q: ng.IQService,
    private ErrorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetViewsRequest, ServerApiMapper.views);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getView', ViewMapper.mapDetail);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public static isViewDetailInfo(item: any): item is IViewDetailInfo {
    return item.contentType &&
           item.contentType === ViewDefinition.contentType &&
           (<IViewDetailInfo>item).hasOwnProperty('sheetType');
  }

  public static isViewBase(item: any): item is IViewBase {
    return item.contentType &&
           item.contentType === ViewDefinition.contentType &&
           item.hasOwnProperty('workbook');
  }

  public all(): ResourceQuery<IViewInfo> {
    return this.allQuery;
  }

  public getById(id: string): IPromise<IViewDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public getByPath(path: string): IPromise<IViewDetailInfo> {
    return this.server.sendRequest(new GetViewByPathRequest(path))
      .then((result: IGetViewByPathResult) => {
        const error = result.errors && result.errors[0] && result.errors[0].code;
        if (error) {
          return this.$q.reject(error);
        } else {
          return this.$q.when(ViewMapper.mapDetail(result));
        }
      });
  }

  public forWorkbook(workbook: IWorkbookBase | IWorkbookParentInfo): ResourceQuery<IViewInfo> {
    return this.all().withFilters(ServerFilterClauses.create({ workbook: workbook.id }));
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'view');
  }

  // Actions

  public actions(id: string): IPromise<IViewActions> {
    return this.server.sendRequest(new GetViewActionsRequest(id));
  }

  public rename(id: string, name: string): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new RenameViewRequest(id, name));
  }

  public hide(ids: string[]): IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new HideViewsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public addTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new AddTagsToViewsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public removeTags(ids: string[], tags: string[]): IPromise<IResult> {
    return (tags.length > 0)
      ? this.ErrorNotifyingServer.sendRequestsAndDelay(
          RequestHelper.buildRequests2(ids, tags,
            (idsBlock, tagsBlock) => new RemoveTagsFromViewsRequest(idsBlock, tagsBlock)),
          ResultHelper.combineResults)
      : this.$q.when({});
  }

  public changeTags(ids: string[], tagsToAdd: string[], tagsToRemove: string[]): IPromise<IResult> {
    return this.removeTags(ids, tagsToRemove)
      .then((result: IResult) => (ResultHelper.hasErrors(result) ? result : this.addTags(ids, tagsToAdd)));
  }

  public markRecentlyViewed(id: string) {
    return this.server.sendRequest(new MarkRecentlyViewedRequest({
      objectId: id,
      objectType: RecentlyViewedObjectType.View
    }));
  }

}

Module.instance().service('Views', Views);
